import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useStores } from "@/stores";
import type { TablesInsert } from "@/types/supabase-helpers";

interface CreateDesignDisciplineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateDesignDisciplineDialog({
  open,
  onOpenChange,
}: CreateDesignDisciplineDialogProps) {
  const { designDisciplinesStore } = useStores();
  const [disciplineName, setDisciplineName] = useState("");
  const [description, setDescription] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const resetForm = () => {
    setDisciplineName("");
    setDescription("");
  };

  const handleCreate = async () => {
    if (!disciplineName.trim()) {
      toast.error("Discipline name is required");
      return;
    }

    setIsCreating(true);
    try {
      const newDiscipline: TablesInsert<"design_disciplines"> = {
        name: disciplineName.trim(),
        description: description.trim() || null,
      };

      await designDisciplinesStore.createDiscipline(newDiscipline);
      toast.success("Design discipline created successfully");
      resetForm();
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to create design discipline");
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isCreating) {
      resetForm();
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Design Discipline</DialogTitle>
          <DialogDescription>
            Add a new design discipline for document classification (27 Estonian disciplines).
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="discipline_name">Discipline Name *</Label>
            <Input
              id="discipline_name"
              value={disciplineName}
              onChange={(e) => setDisciplineName(e.target.value)}
              placeholder="e.g., Arhitektuurne osa"
              disabled={isCreating}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              className="min-h-[80px]"
              disabled={isCreating}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={isCreating}>
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Discipline"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
